import React, { useState, useEffect } from "react";
import { Box, CircularProgress, Alert } from "@mui/material";
import axios from "axios";
import { getAuthHeaders } from "../../../utils/auth";

import StatisticsFilters from "./StatisticsFilters";
import RevenueStatistics from "./RevenueStatistics";
import PopularStatistics from "./PopularStatistics";
import CostsStatistics from "./CostsStatistics";

// Helper function to format date for input field (YYYY-MM-DD)
const formatDateForInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Calculate default "this month" date range
const getThisMonthDates = () => {
  const today = new Date();
  const from = new Date(today.getFullYear(), today.getMonth(), 1);
  const to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  return {
    from: formatDateForInput(from),
    to: formatDateForInput(to)
  };
};

const Statistics = ({ tab = "revenue" }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dashboardData, setDashboardData] = useState(null);
  const [costData, setCostData] = useState(null);
  
  // Date range state - default to this month
  const defaultDates = getThisMonthDates();
  const [dateFrom, setDateFrom] = useState(defaultDates.from);
  const [dateTo, setDateTo] = useState(defaultDates.to);
  const [selectedPreset, setSelectedPreset] = useState("thisMonth");

  const fetchStatistics = async (customDateFrom = null, customDateTo = null) => {
    setLoading(true);
    setError("");
    try {
      const headers = await getAuthHeaders();
      const params = new URLSearchParams();
      
      const fromDate = customDateFrom !== null ? customDateFrom : dateFrom;
      const toDate = customDateTo !== null ? customDateTo : dateTo;
      
      if (fromDate) params.append("date_from", new Date(fromDate).toISOString());
      if (toDate) params.append("date_to", new Date(toDate).toISOString());
      
      const [dashboardResponse, costResponse] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_BACKEND_ADDRESS}/statistics/dashboard?${params.toString()}`,
          { headers }
        ),
        axios.get(
          `${process.env.REACT_APP_BACKEND_ADDRESS}/costs/calculate?${params.toString()}`,
          { headers }
        )
      ]);

      if (dashboardResponse.status === 200) {
        setDashboardData(dashboardResponse.data);
      }
      if (costResponse.status === 200) {
        setCostData(costResponse.data);
      }
    } catch (err) {
      console.error("Failed to fetch statistics", err);
      if (err.response && (err.response.status === 403 || err.response.status === 401)) {
        setError("Brak uprawnień do pobrania statystyk");
      } else {
        setError("Nie udało się załadować statystyk");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatistics();
  }, []);

  const handleRefresh = () => {
    fetchStatistics();
  };

  const handleClearDates = () => {
    setDateFrom("");
    setDateTo("");
    setSelectedPreset("");
    fetchStatistics("", "");
  };

  // Get start of week (Monday)
  const getStartOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  };

  // Get end of week (Sunday)
  const getEndOfWeek = (date) => {
    const start = getStartOfWeek(date);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    return end;
  };

  // Preset date range handlers
  const handlePresetSelection = (preset) => {
    const today = new Date();
    let from, to;

    switch (preset) {
      case 'thisWeek':
        from = getStartOfWeek(today);
        to = getEndOfWeek(today);
        break;
      
      case 'lastWeek':
        const lastWeek = new Date(today);
        lastWeek.setDate(today.getDate() - 7);
        from = getStartOfWeek(lastWeek);
        to = getEndOfWeek(lastWeek);
        break;
      
      case 'thisMonth':
        from = new Date(today.getFullYear(), today.getMonth(), 1);
        to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      
      case 'lastMonth':
        from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        to = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      
      case 'thisYear':
        from = new Date(today.getFullYear(), 0, 1);
        to = new Date(today.getFullYear(), 11, 31);
        break;
      
      case 'last30Days':
        from = new Date(today);
        from.setDate(today.getDate() - 30);
        to = today;
        break;
      
      case 'allTime':
        setDateFrom("");
        setDateTo("");
        setSelectedPreset('allTime');
        fetchStatistics("", "");
        return;
      
      default:
        return;
    }

    const formattedFrom = formatDateForInput(from);
    const formattedTo = formatDateForInput(to);
    
    setDateFrom(formattedFrom);
    setDateTo(formattedTo);
    setSelectedPreset(preset);
    
    fetchStatistics(formattedFrom, formattedTo);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Cały okres";
    return new Date(dateString).toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const renderTabContent = () => {
    switch (tab) {
      case "revenue":
        return <RevenueStatistics dashboardData={dashboardData} formatCurrency={formatCurrency} />;
      case "popular":
        return <PopularStatistics dashboardData={dashboardData} formatCurrency={formatCurrency} />;
      case "costs":
        return <CostsStatistics costData={costData} formatCurrency={formatCurrency} />;
      default:
        return <RevenueStatistics dashboardData={dashboardData} formatCurrency={formatCurrency} />;
    }
  };

  return (
    <Box sx={{ width: "100%", p: { xs: 1, sm: 2, md: 3 } }}>
      {/* Shared Filters */}
      <StatisticsFilters
        dateFrom={dateFrom}
        setDateFrom={setDateFrom}
        dateTo={dateTo}
        setDateTo={setDateTo}
        selectedPreset={selectedPreset}
        setSelectedPreset={setSelectedPreset}
        loading={loading}
        onRefresh={handleRefresh}
        onClearDates={handleClearDates}
        onPresetSelection={handlePresetSelection}
        formatDate={formatDate}
      />

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Tab Content */}
      {!loading && !error && renderTabContent()}
    </Box>
  );
};

export default Statistics;
